import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/crypto-data";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Eye, CheckCircle, XCircle, Image, Search, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface Transaction {
  id: string;
  user_id: string;
  crypto_name: string;
  crypto_symbol: string;
  amount: number;
  price_usd: number;
  total_usd: number;
  network_fee: number;
  status: string;
  created_at: string;
  card_type: string;
  card_last_four: string;
  network: string;
  wallet_address: string;
  payment_proof_url?: string;
  admin_notes?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  transaction_number?: string;
}

const PROFIT_MARGIN = 0.20;

const AdminTransactions = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [selectedProofUrl, setSelectedProofUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching transactions:", error);
      } else {
        setTransactions(data || []);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleApprove = async (transaction: Transaction) => {
    if (!user) return;
    setActionLoading(true);

    try {
      const { error } = await supabase
        .from("transactions")
        .update({
          status: "completed",
          admin_notes: adminNotes,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq("id", transaction.id);

      if (error) throw error;

      toast({
        title: "تمت الموافقة",
        description: "تم الموافقة على المعاملة بنجاح",
      });

      setSelectedTransaction(null);
      setAdminNotes("");
      fetchTransactions();
    } catch (error) {
      console.error("Error approving transaction:", error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء الموافقة على المعاملة",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (transaction: Transaction) => {
    if (!user) return;
    setActionLoading(true);

    try {
      const { error } = await supabase
        .from("transactions")
        .update({
          status: "failed",
          admin_notes: adminNotes,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq("id", transaction.id);

      if (error) throw error;

      toast({
        title: "تم الرفض",
        description: "تم رفض المعاملة",
      });

      setSelectedTransaction(null);
      setAdminNotes("");
      fetchTransactions();
    } catch (error) {
      console.error("Error rejecting transaction:", error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء رفض المعاملة",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-crypto-green/20 text-crypto-green">مكتمل</Badge>;
      case "pending":
        return <Badge className="bg-crypto-gold/20 text-crypto-gold">قيد المراجعة</Badge>;
      case "failed":
        return <Badge className="bg-destructive/20 text-destructive">مرفوض</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const filteredTransactions = transactions.filter(t => {
    const q = searchQuery.toLowerCase();
    if (!q) return true;
    return (
      t.transaction_number?.toLowerCase().includes(q) ||
      t.user_id.toLowerCase().includes(q) ||
      t.crypto_symbol.toLowerCase().includes(q) ||
      t.wallet_address?.toLowerCase().includes(q) ||
      t.id.toLowerCase().includes(q)
    );
  });

  const pendingTransactions = filteredTransactions.filter(t => t.status === "pending");
  const otherTransactions = filteredTransactions.filter(t => t.status !== "pending");

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="بحث برقم المعاملة، المستخدم، العملة..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pr-10 bg-input border-border"
          dir="rtl"
        />
      </div>

      {/* Pending Transactions */}
      {pendingTransactions.length > 0 && (
        <Card className="glass border-border border-crypto-gold/30">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-crypto-gold animate-pulse" />
              معاملات تنتظر المراجعة ({pendingTransactions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-right text-muted-foreground">رقم المعاملة</TableHead>
                    <TableHead className="text-right text-muted-foreground">التاريخ</TableHead>
                    <TableHead className="text-right text-muted-foreground">العملة</TableHead>
                    <TableHead className="text-right text-muted-foreground">الكمية</TableHead>
                    <TableHead className="text-right text-muted-foreground">المبلغ</TableHead>
                    <TableHead className="text-right text-muted-foreground">الإثبات</TableHead>
                    <TableHead className="text-right text-muted-foreground">الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingTransactions.map((transaction) => (
                    <TableRow key={transaction.id} className="border-border">
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">{transaction.transaction_number || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-foreground">
                        {formatDate(transaction.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {transaction.crypto_symbol}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-foreground">
                        {transaction.amount.toFixed(6)}
                      </TableCell>
                      <TableCell className="text-foreground">
                        {formatCurrency(transaction.total_usd)}
                      </TableCell>
                      <TableCell>
                        {transaction.payment_proof_url ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedProofUrl(transaction.payment_proof_url || "");
                              setProofDialogOpen(true);
                            }}
                          >
                            <Image className="w-4 h-4 text-primary" />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">لا يوجد</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedTransaction(transaction)}
                            >
                              مراجعة
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg" dir="rtl">
                            <DialogHeader>
                              <DialogTitle>مراجعة المعاملة</DialogTitle>
                            </DialogHeader>
                            {selectedTransaction && (
                              <div className="space-y-4">
                                {/* Transaction Details */}
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">العملة</p>
                                    <p className="font-medium">{selectedTransaction.crypto_name} ({selectedTransaction.crypto_symbol})</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">الكمية</p>
                                    <p className="font-medium">{selectedTransaction.amount.toFixed(6)}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">المبلغ الإجمالي</p>
                                    <p className="font-medium">{formatCurrency(selectedTransaction.total_usd)}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">الشبكة</p>
                                    <p className="font-medium">{selectedTransaction.network}</p>
                                  </div>
                                </div>

                                <div>
                                  <p className="text-muted-foreground text-sm">عنوان المحفظة (لإرسال العملة)</p>
                                  <p className="font-mono text-xs break-all bg-secondary/50 p-2 rounded mt-1">{selectedTransaction.wallet_address}</p>
                                </div>

                                {/* Payment Proof */}
                                {selectedTransaction.payment_proof_url && (
                                  <div>
                                    <p className="text-muted-foreground text-sm mb-2">إثبات الدفع</p>
                                    <img
                                      src={selectedTransaction.payment_proof_url}
                                      alt="إثبات الدفع"
                                      className="w-full max-h-64 object-contain rounded-lg border border-border"
                                    />
                                  </div>
                                )}

                                {/* Admin Notes */}
                                <div className="space-y-2">
                                  <Label>ملاحظات المسؤول (اختياري)</Label>
                                  <Textarea
                                    placeholder="أضف ملاحظات حول هذه المعاملة..."
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    className="bg-input border-border"
                                  />
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                  <Button
                                    onClick={() => handleApprove(selectedTransaction)}
                                    disabled={actionLoading}
                                    className="flex-1 bg-crypto-green hover:bg-crypto-green/90"
                                  >
                                    <CheckCircle className="w-4 h-4 ml-2" />
                                    {actionLoading ? "جاري..." : "موافقة"}
                                  </Button>
                                  <Button
                                    onClick={() => handleReject(selectedTransaction)}
                                    disabled={actionLoading}
                                    variant="destructive"
                                    className="flex-1"
                                  >
                                    <XCircle className="w-4 h-4 ml-2" />
                                    {actionLoading ? "جاري..." : "رفض"}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Transactions */}
      <Card className="glass border-border">
        <CardHeader>
          <CardTitle className="text-foreground">جميع المعاملات</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              لا توجد معاملات حتى الآن
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-right text-muted-foreground">رقم المعاملة</TableHead>
                    <TableHead className="text-right text-muted-foreground">التاريخ</TableHead>
                    <TableHead className="text-right text-muted-foreground">العملة</TableHead>
                    <TableHead className="text-right text-muted-foreground">الكمية</TableHead>
                    <TableHead className="text-right text-muted-foreground">المبلغ</TableHead>
                    <TableHead className="text-right text-muted-foreground">الربح</TableHead>
                    <TableHead className="text-right text-muted-foreground">الحالة</TableHead>
                    <TableHead className="text-right text-muted-foreground">التفاصيل</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((transaction) => {
                    const baseAmount = transaction.total_usd - transaction.network_fee;
                    const profit = (baseAmount * PROFIT_MARGIN) / (1 + PROFIT_MARGIN);

                    return (
                      <TableRow key={transaction.id} className="border-border">
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">{transaction.transaction_number || '-'}</Badge>
                        </TableCell>
                        <TableCell className="text-foreground">
                          {formatDate(transaction.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">
                              {transaction.crypto_symbol}
                            </span>
                            <span className="text-muted-foreground text-sm">
                              {transaction.crypto_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-foreground">
                          {transaction.amount.toFixed(6)}
                        </TableCell>
                        <TableCell className="text-foreground">
                          {formatCurrency(transaction.total_usd)}
                        </TableCell>
                        <TableCell className="text-crypto-green font-medium">
                          +{formatCurrency(profit)}
                        </TableCell>
                        <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedTransaction(transaction)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md" dir="rtl">
                              <DialogHeader>
                                <DialogTitle>تفاصيل المعاملة</DialogTitle>
                              </DialogHeader>
                              {selectedTransaction && (
                                <div className="space-y-4 text-sm">
                                  <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-lg">
                                    <span className="text-muted-foreground">رقم المعاملة:</span>
                                    <Badge variant="outline" className="font-mono">{selectedTransaction.transaction_number || '-'}</Badge>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-muted-foreground">معرف النظام</p>
                                      <p className="font-mono text-xs break-all">{selectedTransaction.id}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">رقم المستخدم</p>
                                      <p className="font-mono text-xs break-all">{selectedTransaction.user_id}</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-muted-foreground">العملة</p>
                                      <p>{selectedTransaction.crypto_name} ({selectedTransaction.crypto_symbol})</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">الكمية</p>
                                      <p>{selectedTransaction.amount.toFixed(6)}</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-muted-foreground">سعر الوحدة</p>
                                      <p>{formatCurrency(selectedTransaction.price_usd)}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">المبلغ الإجمالي</p>
                                      <p>{formatCurrency(selectedTransaction.total_usd)}</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-muted-foreground">رسوم الشبكة</p>
                                      <p>{formatCurrency(selectedTransaction.network_fee)}</p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">الشبكة</p>
                                      <p>{selectedTransaction.network || "-"}</p>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">عنوان المحفظة</p>
                                    <p className="font-mono text-xs break-all">{selectedTransaction.wallet_address || "-"}</p>
                                  </div>
                                  {selectedTransaction.admin_notes && (
                                    <div>
                                      <p className="text-muted-foreground">ملاحظات المسؤول</p>
                                      <p className="bg-secondary/50 p-2 rounded mt-1">{selectedTransaction.admin_notes}</p>
                                    </div>
                                  )}
                                  {selectedTransaction.payment_proof_url && (
                                    <div>
                                      <p className="text-muted-foreground mb-2">إثبات الدفع</p>
                                      <img
                                        src={selectedTransaction.payment_proof_url}
                                        alt="إثبات الدفع"
                                        className="w-full max-h-48 object-contain rounded-lg border border-border"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proof Image Dialog */}
      <Dialog open={proofDialogOpen} onOpenChange={setProofDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>إثبات الدفع</DialogTitle>
          </DialogHeader>
          <img
            src={selectedProofUrl}
            alt="إثبات الدفع"
            className="w-full max-h-[70vh] object-contain"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTransactions;
